import { Component } from '@theme/component';
import { debounce, fetchConfig } from '@theme/utilities';

const NIP_WEIGHTS = [6, 5, 7, 2, 3, 4, 5, 6, 7];

/**
 * Strips separators and an optional "PL" prefix from a NIP.
 * @param {string} value - The raw input value.
 * @returns {string} The normalized value.
 */
function normalizeNip(value) {
  return value.replace(/[\s-]/g, '').replace(/^PL/i, '');
}

/**
 * Checks the length and the control digit of a NIP.
 * @param {string} nip - A normalized NIP.
 * @returns {boolean} Whether the NIP is valid.
 */
function isValidNip(nip) {
  if (!/^\d{10}$/.test(nip)) return false;

  let sum = 0;
  for (const [index, weight] of NIP_WEIGHTS.entries()) {
    sum += weight * Number(nip[index]);
  }

  return sum % 11 === Number(nip[9]);
}

/**
 * A custom element that saves the buyer's NIP as a cart attribute.
 */
class CartNip extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /**
   * Validates the NIP and saves it to the cart.
   * @param {InputEvent} event - The input event in our text field.
   */
  updateNip = debounce(async (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const nip = normalizeNip(input.value);
    const isInvalid = nip !== '' && !isValidNip(nip);
    this.#setValidity(input, isInvalid);
    if (isInvalid) return;

    this.#activeFetch?.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ attributes: { NIP: nip } }),
      });

      await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });
    } catch (error) {
      // The field is part of the cart form, so the value still reaches the order on submit.
      if (error instanceof Error && error.name !== 'AbortError') console.error(error);
    } finally {
      if (this.#activeFetch === abortController) this.#activeFetch = null;
    }
  }, 300);

  /**
   * Blocks the cart form on an invalid NIP and swaps the hint for the error.
   * @param {HTMLInputElement} input - The NIP field.
   * @param {boolean} isInvalid - Whether the current value is invalid.
   */
  #setValidity(input, isInvalid) {
    const message = isInvalid ? this.dataset.invalid ?? '' : '';
    input.setCustomValidity(message);
    input.setAttribute('aria-invalid', String(isInvalid));

    const hint = this.querySelector('.cart-nip__hint');
    if (hint) hint.textContent = isInvalid ? message : this.dataset.hint ?? '';
  }
}

if (!customElements.get('cart-nip')) {
  customElements.define('cart-nip', CartNip);
}
